if (!customElements.get('superstore-mobile-drawer')) {
  class SuperstoreMobileDrawer extends HTMLElement {
    constructor() {
      super();
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleAccordionToggle = this.handleAccordionToggle.bind(this);
      this.lastTrigger = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.drawerId = this.dataset.drawerId || 'mobile-menu';
      this.drawer = this.querySelector(`#${this.drawerId}`) || document.getElementById(this.drawerId);

      if (!this.drawer) return;

      this.panel = this.drawer.querySelector('[data-mobile-drawer-panel]') || this.drawer.querySelector('.drawer__panel');
      this.triggers = Array.from(this.querySelectorAll(`[data-drawer-trigger="#${this.drawerId}"]`));
      this.closeButtons = Array.from(this.drawer.querySelectorAll('[data-drawer-close]'));
      this.primaryCloseButton = this.closeButtons[0] || null;
      this.accordionDetails = Array.from(this.drawer.querySelectorAll('[data-mobile-menu-accordion] details'));
      this.drawer.setAttribute('aria-hidden', 'true');

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
        trigger.setAttribute('aria-controls', this.drawerId);
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.handleCloseClick);
      });

      this.accordionDetails.forEach((details) => {
        details.addEventListener('toggle', this.handleAccordionToggle);
      });

      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('focusin', this.handleFocusIn);
    }

    disconnectedCallback() {
      this.triggers?.forEach((trigger) => {
        trigger.removeEventListener('click', this.handleTriggerClick);
      });

      this.closeButtons?.forEach((button) => {
        button.removeEventListener('click', this.handleCloseClick);
      });

      this.accordionDetails?.forEach((details) => {
        details.removeEventListener('toggle', this.handleAccordionToggle);
      });

      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('focusin', this.handleFocusIn);
    }

    syncBodyScrollLock() {
      document.body.classList.toggle('overflow-hidden', Boolean(document.querySelector('[data-drawer].is-open')));
    }

    syncExpandedState(isOpen) {
      this.triggers.forEach((trigger) => {
        const isActiveTrigger = isOpen && trigger === this.lastTrigger;
        trigger.setAttribute('aria-expanded', isActiveTrigger ? 'true' : 'false');
      });
    }

    /* Exclusive accordion: opening a <details> closes its sibling <details>
       elements under the same immediate parent, at every nesting depth, so
       only one branch is ever expanded at a time. */
    handleAccordionToggle(event) {
      const details = event.target;
      if (!details.open) return;

      Array.from(details.parentElement.children)
        .filter((el) => el !== details && el.tagName === 'DETAILS')
        .forEach((sibling) => this.closeDetailsTree(sibling));
    }

    closeDetailsTree(details) {
      details.open = false;
      details.querySelectorAll('details').forEach((nested) => {
        nested.open = false;
      });
    }

    collapseAccordion() {
      this.accordionDetails?.forEach((details) => {
        details.open = false;
      });
    }

    getFocusableElements() {
      if (!this.panel) return [];

      const selectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        'summary'
      ];

      return Array.from(this.panel.querySelectorAll(selectors.join(','))).filter(
        (element) => !element.hidden && !element.closest('[hidden]') && element.offsetParent !== null
      );
    }

    openDrawer(trigger) {
      this.lastTrigger = trigger || null;

      this.collapseAccordion();
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.syncExpandedState(true);
      this.syncBodyScrollLock();
      window.theme?.trapFocus(this.drawer);

      window.requestAnimationFrame(() => {
        const nextFocusTarget = this.primaryCloseButton || this.panel;
        nextFocusTarget?.focus({ preventScroll: true });
      });

      this.dispatchEvent(
        new CustomEvent('superstore:drawer-open', {
          bubbles: true,
          detail: {
            drawerId: this.drawerId
          }
        })
      );
    }

    closeDrawer({ restoreFocus = true } = {}) {
      if (!this.drawer.classList.contains('is-open')) return;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.syncExpandedState(false);
      this.syncBodyScrollLock();
      window.theme?.releaseFocus(this.drawer);

      if (restoreFocus && this.lastTrigger?.isConnected) {
        this.lastTrigger.focus({ preventScroll: true });
      }

      this.dispatchEvent(
        new CustomEvent('superstore:drawer-close', {
          bubbles: true,
          detail: {
            drawerId: this.drawerId
          }
        })
      );
    }

    handleTriggerClick(event) {
      event.__superstoreDrawerHandled = true;
      event.stopPropagation();

      if (event.currentTarget.tagName === 'A') {
        event.preventDefault();
      }

      this.openDrawer(event.currentTarget);
    }

    handleCloseClick(event) {
      event.__superstoreDrawerHandled = true;
      event.stopPropagation();
      this.closeDrawer();
    }

    handleKeydown(event) {
      if (!this.drawer.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        this.panel?.focus({ preventScroll: true });
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus({ preventScroll: true });
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus({ preventScroll: true });
      }
    }

    handleFocusIn(event) {
      if (!this.drawer.classList.contains('is-open')) return;
      if (this.panel?.contains(event.target)) return;

      const focusableElements = this.getFocusableElements();
      const nextFocusTarget = focusableElements[0] || this.primaryCloseButton || this.panel;
      nextFocusTarget?.focus({ preventScroll: true });
    }
  }

  customElements.define('superstore-mobile-drawer', SuperstoreMobileDrawer);
}
